import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

export interface GeocodeResult {
  Id: number;
  Label: string;
  Description: string;
  Latitude: number;
  Longitude: number;
}

interface NominatimAddress {
  house_number?: string;
  road?: string;
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  state?: string;
  postcode?: string;
}

interface NominatimPlace {
  place_id: number;
  lat: string;
  lon: string;
  name?: string;
  display_name: string;
  address?: NominatimAddress;
}

// Location.Latitude/Longitude are stored as decimal(10, 6).
const COORDINATE_DECIMALS = 6;

@Injectable({
  providedIn: 'root',
})
export class GeocodingService {
  // Built on HttpBackend so the app's interceptors never attach our JWT to a third-party request.
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly searchEndpoint = 'https://nominatim.openstreetmap.org/search';

  /**
   * Looks up US addresses with OpenStreetMap's Nominatim. Its usage policy allows one request per second
   * and no search-as-you-type, so only call this on an explicit search.
   * Emits null when the lookup fails, and an empty array when nothing matched.
   */
  searchUsAddress(query: string): Observable<GeocodeResult[] | null> {
    const params = new HttpParams({
      fromObject: {
        q: query,
        format: 'jsonv2',
        addressdetails: 1,
        countrycodes: 'us',
        limit: 5,
        'accept-language': 'en-US',
      },
    });

    return this.http.get<NominatimPlace[]>(this.searchEndpoint, { params }).pipe(
      map((places) => places.map(mapToGeocodeResult)),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }
}

export function roundCoordinate(value: number): number {
  return Number(value.toFixed(COORDINATE_DECIMALS));
}

function mapToGeocodeResult(place: NominatimPlace): GeocodeResult {
  return {
    Id: place.place_id,
    Label: formatUsAddress(place),
    Description: place.display_name,
    Latitude: roundCoordinate(Number(place.lat)),
    Longitude: roundCoordinate(Number(place.lon)),
  };
}

function formatUsAddress(place: NominatimPlace): string {
  const address = place.address ?? {};
  const street = [address.house_number, address.road].filter(Boolean).join(' ');
  const city = address.city ?? address.town ?? address.village ?? address.hamlet;
  const stateAndZip = [address.state, address.postcode].filter(Boolean).join(' ');

  return [street || place.name, city, stateAndZip].filter(Boolean).join(', ') || place.display_name;
}
